import React, { memo, useState } from 'react';

import { useAppSelector } from '../../store/hooks';
import { Hosts } from '../../constants/hostName';

import BrewboundLogo from '../../assets/Brewbound-Logo.svg';
import NoshLogo from '../../assets/NOSH-Logo.svg';
import BevnetLogo from '../../assets/BevNET-Logo.svg';

// Cross-brand reassurance strip. The Jobs SPA is fully CPGJobs-branded, but
// visitors arrive from the individual brand sites (a Post-a-Job click on
// brewbound.com lands on /PostJobListing/brewbound, etc.). Without an
// acknowledgement of where they came from, a Brewbound/Nosh/BevNET visitor can
// think they clicked wrong and bounce. This slim strip names the source brand
// and reassures them their board is now powered by CPGJobs. The source brand comes from
// `pathnameSettings` (route-driven via SiteAddress → slicePathname), so no new
// plumbing is needed. Dismissible — the choice persists in localStorage so a
// returning visitor isn't reminded on every page.

const DISMISS_KEY = 'cpgjobs_xbrand_dismissed';

interface Brand {
  logo: string;
  name: string;
}

const BRANDS: Record<number, Brand> = {
  [Hosts.BREWBOUND]: { logo: BrewboundLogo, name: 'Brewbound' },
  [Hosts.NOSH]: { logo: NoshLogo, name: 'Nosh' },
  [Hosts.BEVNET]: { logo: BevnetLogo, name: 'BevNET' }
};

const readDismissed = (): boolean => {
  try {
    return window.localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
};

const CrossBrandBanner = () => {
  const { siteNumber, color } = useAppSelector(({ pathnameSettings }) => ({
    siteNumber: pathnameSettings.siteNumber,
    color: pathnameSettings.color
  }));

  const [dismissed, setDismissed] = useState<boolean>(readDismissed);

  const onDismiss = () => {
    try {
      window.localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // ignore — worst case the strip reappears next load
    }
    setDismissed(true);
  };

  if (dismissed) return null;

  // Fall back to the BevNET variant if siteNumber is missing — never render empty.
  const brand = BRANDS[siteNumber] || BRANDS[Hosts.BEVNET];

  return (
    <div
      className="cpg-xbrand"
      style={{ ['--xbrand-accent' as string]: color } as React.CSSProperties}
    >
      <div className="cpg-xbrand__inner">
        <span
          className="cpg-xbrand__accent"
          aria-hidden="true"
        />
        <img
          className="cpg-xbrand__logo"
          src={brand.logo}
          alt={brand.name}
        />
        <span
          className="cpg-xbrand__divider"
          aria-hidden="true"
        />
        <p className="cpg-xbrand__copy">
          Looking for the {brand.name} job board?{' '}
          <strong>You&rsquo;re in the right place</strong>. It&rsquo;s now powered by CPG Jobs,
          BevNET CPG Media&rsquo;s new hiring platform, putting your roles in front of{' '}
          <strong>more quality candidates</strong> across the industry.
        </p>
        <button
          type="button"
          className="cpg-xbrand__dismiss"
          onClick={onDismiss}
          aria-label="Dismiss"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M6 6l12 12M18 6L6 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default memo(CrossBrandBanner);
